// What the sections dress (PLAN-UI §28.3, U6): the section heading, the rank
// grid, the climb/craft tiles, a store's taps, a turret's stat rows and the
// sacrifice row. The card's own primitives are `styles.ts`.

import { SectionTitle, styled, Tile, TileGlyph } from "../kit";

/** The mock's `#ctx h4`. */
export const Title = styled(SectionTitle)(({ theme }) => ({
  display: "block",
  marginBottom: theme.kit.space.xs
}));

/**
 * Five rank tiles across a 240px card.
 *
 * `Tile`'s rail variant is a fixed 46px square — five of those are 230px
 * before a gap, against 210px of content here, and they wrapped four-and-one.
 * A grid of equal columns keeps the tile family's edge, wash and height and
 * spends the width it actually has: about 38px a tile, which a 19px glyph
 * sits in comfortably.
 */
export const Ranks = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 4
});

/** Doubled, so this beats `Tile`'s own width whichever order emotion emits. */
export const RankTile = styled(Tile)({ "&&": { width: "auto", minWidth: 0 } });

/**
 * The rank glyph, in the machine's own tone.
 *
 * This is what replaced the `save-for` headline: the lit tile is the readout,
 * so it is the brand while its coalition can pay and rust sunk into the dim
 * ink while it is short. An unlit tile has no state and takes neither.
 */
export const RankGlyph = styled(TileGlyph)(({ theme }) => ({
  "&.ready": { color: theme.kit.color.brand },
  "&.short": {
    color: `color-mix(in srgb, ${theme.kit.color.rust} 55%, ${theme.kit.color.inkDim})`
  }
}));

export const Options = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  alignItems: "flex-start"
});

export const Option = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: 66,
  gap: 2
});

export const TileNote = styled("span")(({ theme }) => ({
  fontSize: 10,
  lineHeight: 1.3,
  textAlign: "center",
  color: theme.kit.color.inkDim
}));

/** What a climb tile buys — four tiles share one rank's glyph, so the name tells them apart. */
export const TileName = styled("span")(({ theme }) => ({
  font: `600 10.5px ${theme.kit.font.display}`,
  lineHeight: 1.2,
  textAlign: "center",
  color: theme.kit.color.ink
}));

/** A reason every tile in the section shares, said once above them. */
export const SectionNote = styled("div")(({ theme }) => ({
  fontSize: 11,
  lineHeight: 1.35,
  color: theme.kit.color.inkDim,
  marginBottom: theme.kit.space.xs
}));

export const Taps = styled("div")({
  display: "grid",
  gridTemplateColumns: "auto 1fr 1fr auto",
  gap: "4px 8px",
  fontSize: 12,
  alignItems: "center"
});

/** The grid's rows are flat children, so a row is a fragment of four cells. */
export const TapRow = styled("div")({ display: "contents" });

export const Head = styled("span")(({ theme }) => ({
  font: `600 9.5px ${theme.kit.font.display}`,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: theme.kit.color.inkDim,
  textAlign: "center"
}));

export const Count = styled("span")(({ theme }) => ({
  font: `700 11px ${theme.kit.font.display}`,
  color: theme.kit.color.inkDim,
  textAlign: "right"
}));

/**
 * A switch. Lit is the accent, off is a dash — the mock's own pair, and a dash
 * rather than an empty box because "closed" is a setting and not an absence.
 */
export const TapCell = styled("button")(({ theme }) => {
  const c = theme.kit.color;
  return {
    textAlign: "center",
    border: `2px solid ${c.line}`,
    borderRadius: theme.kit.radius.sm,
    background: "transparent",
    color: c.ink,
    padding: "3px 0",
    font: `700 11px ${theme.kit.font.display}`,
    cursor: "pointer",
    "&:hover": { borderColor: c.lineHi },
    "&:focus-visible": { outline: `2px solid ${c.brand}`, outlineOffset: 2 },
    '&[aria-pressed="true"]': {
      borderColor: c.brand,
      color: c.brand,
      background: `color-mix(in srgb, ${c.brand} 14%, ${c.ground})`
    },
    // Off — off-turn, since a tap has no other reason to be. `Tile`'s rule and
    // `Tile`'s numbers: still hoverable, because the reason is the whole
    // content of an off control. After `aria-pressed` so a lit tap dims too.
    '&[aria-disabled="true"]': { opacity: 0.35, cursor: "default" }
  };
});

export const StatRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginTop: 3
});

export const Caption = styled("span")(({ theme }) => ({
  width: 52,
  flexShrink: 0,
  font: `600 9.5px ${theme.kit.font.display}`,
  letterSpacing: ".06em",
  textTransform: "uppercase",
  color: theme.kit.color.inkDim
}));

/** A full-width policy row — a turret's stat, which is a sentence not a word. */
export const Wide = styled("button")(({ theme }) => {
  const c = theme.kit.color;
  return {
    flex: 1,
    minWidth: 0,
    textAlign: "left",
    border: `2px solid ${c.line}`,
    borderRadius: theme.kit.radius.sm,
    background: "transparent",
    color: c.ink,
    padding: "3px 7px",
    fontFamily: theme.kit.font.body,
    fontWeight: 700,
    fontSize: 11.5,
    cursor: "pointer",
    "&:hover": { borderColor: c.lineHi },
    "&:focus-visible": { outline: `2px solid ${c.brand}`, outlineOffset: 2 },
    '&[aria-pressed="true"]': {
      borderColor: c.brand,
      color: c.brand,
      background: `color-mix(in srgb, ${c.brand} 14%, ${c.ground})`
    },
    '&[aria-disabled="true"]': { opacity: 0.35, cursor: "default", borderColor: c.line }
  };
});

/** The mock's bottom row: a sentence and a small button, pushed apart. */
export const Between = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  fontSize: 12.5
});
